import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import i18n from "@/i18n.ts";
import { ruleGroupsSchema } from "@/components/apply-to/rule-groups.schema.ts";
import { getSurveyRuleGroups } from "@/types/survey.types.ts";
import { SurveyEditSidebar } from "@/pages/talent/surveys/edit/components/SurveyEditSidebar.tsx";
import {
  SurveyEditPageProps,
  useSurveyEditPage,
} from "@/pages/talent/surveys/edit/pages/survey-edit-pages.tsx";
import { SurveyEditTitleSlot } from "@/pages/talent/surveys/edit/pages/SurveyEditTitleSlot.tsx";
import { SurveyEditPageStep } from "@/pages/talent/surveys/edit/survey-edit.ts";
import {
  useEmployeeMe,
  useEmployees,
  useEmployeesByIds,
} from "@/queries/employees/use-employees.query.ts";
import { useSurveyUpdateDetails } from "@/queries/surveys/use-surveys.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEmployeeFilter, EmployeeFilterDefault } from "@/types/employees/employee.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FontAwesomeIcon,
  FullScreenPage,
  Input,
  Multiselect,
  Select,
  SelectItem,
  Stack,
  Switch,
  Textarea,
  Title,
} from "@skailer-com/uikit";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";
import { z } from "zod";

const buildSurveyEditDetailsSchema = () =>
  z
    .object({
      title: z.string().min(1, i18n.t("errors:field_required")),
      participants_rule_groups: ruleGroupsSchema(),
      emoji: z.string().nullable(),
      description: z.string().nullable(),
      owner_ids: z.array(z.number()).optional(),
      use_ai: z.boolean().optional(),
      invitation_from_employee_id: z.number().nullable(),
      invitation_subject: z.string().nullable(),
      invitation_body: z.string().nullable(),
      share_employee_rule_groups: ruleGroupsSchema(),
      is_share_employee_after_completion: z.boolean().optional(),
    })
    .superRefine((data, context) => {
      if (data.invitation_body?.trim() && !data.invitation_subject?.trim()) {
        context.addIssue({
          code: "custom",
          path: ["invitation_subject"],
          message: i18n.t("errors:field_required"),
        });
      }
      if (data.invitation_subject?.trim() && !data.invitation_body?.trim()) {
        context.addIssue({
          code: "custom",
          path: ["invitation_body"],
          message: i18n.t("errors:field_required"),
        });
      }
    });

export const SurveyEditDetailsPage = ({ survey }: SurveyEditPageProps) => {
  const { t } = useTranslation();
  const participantConditionTypes = useRuleConditionTypes(RuleRegistryPlace.surveyParticipants);
  const shareEmployeeConditionTypes = useRuleConditionTypes(RuleRegistryPlace.surveyShareEmployees);

  const { confirmExit, navigateToStep } = useSurveyEditPage();
  const { renderEmployeeName } = useFormatter();
  const { isFeatureEnabled } = usePermissions();

  const { data: employeeMe } = useEmployeeMe();

  const canManageOwners = useMemo(() => {
    if (!employeeMe?.id || !survey.owners?.[0]) return false;

    return survey.owners[0].id === employeeMe.id;
  }, [employeeMe, survey]);

  const ownerOptions = useMemo(() => {
    if (!survey.owners || !employeeMe) return [];

    return survey.owners
      .filter((x) => x.id !== employeeMe.id)
      .map((x) => ({
        id: x.id,
        title: renderEmployeeName(x),
      })) as SelectItem[];
  }, [survey, employeeMe]);

  const [useEmoji, setUseEmoji] = useState(!!survey.emoji);

  const [useEmployeeSharing, setUseEmployeeSharing] = useState<boolean>(
    getSurveyRuleGroups(survey, "share_employee").some((group) => group.rules.length > 0)
  );

  const [useInviteCoOwners, setUseInviteCoOwners] = useState<boolean>(ownerOptions.length > 0);

  const surveyEditDetailsSchema = buildSurveyEditDetailsSchema();

  const {
    control,
    register,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(surveyEditDetailsSchema),
    values: {
      title: survey.title,
      participants_rule_groups: getSurveyRuleGroups(survey, "participants").map((group) => ({
        rules: group.rules.map((rule) => ({ ...rule, value_ids: rule.value_ids ?? [] })),
      })),
      emoji: survey.emoji || emojiDefaults.survey.default,
      description: survey.description,
      owner_ids: ownerOptions.map((x) => x.id as number),
      use_ai: survey.use_ai,
      invitation_from_employee_id: survey.invitation_from_employee?.id ?? survey.owners[0]?.id,
      invitation_subject: survey.invitation_subject,
      invitation_body: survey.invitation_body,
      share_employee_rule_groups: getSurveyRuleGroups(survey, "share_employee").map((group) => ({
        rules: group.rules.map((rule) => ({ ...rule, value_ids: rule.value_ids ?? [] })),
      })),
      is_share_employee_after_completion: survey.is_share_employee_after_completion,
    },
  });
  const surveyUpdate = useSurveyUpdateDetails();

  const onSubmit = handleSubmit(async (data) => {
    const initialUseEmoji = !!survey.emoji;
    const initialUseEmployeeSharing = getSurveyRuleGroups(survey, "share_employee").some(
      (group) => group.rules.length > 0
    );
    const initialUseInviteCoOwners = ownerOptions.length > 0;

    const hasToggleChanges =
      useEmoji !== initialUseEmoji ||
      useEmployeeSharing !== initialUseEmployeeSharing ||
      useInviteCoOwners !== initialUseInviteCoOwners;

    const shouldSave = isDirty || hasToggleChanges;

    if (!shouldSave) {
      navigateToStep(survey.id, SurveyEditPageStep.questions);
      return;
    }

    try {
      await surveyUpdate.mutateAsync({
        id: survey.id,
        payload: {
          ...data,
          emoji: useEmoji ? data.emoji : "",
          owner_ids: canManageOwners ? (useInviteCoOwners ? data.owner_ids : []) : undefined,
          participants_rule_groups: data.participants_rule_groups.filter(
            (group) => group.rules.length > 0
          ),
          share_employee_rule_groups: useEmployeeSharing
            ? data.share_employee_rule_groups.filter((group) => group.rules.length > 0)
            : [],
          is_share_employee_after_completion: useEmployeeSharing
            ? data.is_share_employee_after_completion
            : false,
        },
      });
      analyticsService.trackEvent(analyticEvents.surveys.edited, {
        [analyticProperties.id]: survey.id,
        [analyticProperties.tab]: SurveyEditPageStep.details,
      });
      navigateToStep(survey.id, SurveyEditPageStep.questions);
    } catch {
      // todo handle error
    }
  });

  const [employeeOwnersFilter, setEmployeeOwnersFilter] = useState<ApiEmployeeFilter>(() => ({
    ...EmployeeFilterDefault,
    exclude_ids: employeeMe?.id ? [employeeMe.id] : [],
  }));

  useEffect(() => {
    if (!employeeMe) return;
    setEmployeeOwnersFilter((prev) => ({
      ...prev,
      exclude_ids: [employeeMe.id],
    }));
  }, [employeeMe]);

  const { data: employeesOwnersSearch, isLoading: isEmployeesOwnerIsLoading } = useEmployees(
    employeeOwnersFilter,
    canManageOwners
  );

  const employeesOwnersSearchOptions = useMemo(() => {
    if (employeesOwnersSearch?.items) {
      return employeesOwnersSearch.items.map((x) => {
        return { id: x.id, title: renderEmployeeName(x) };
      }) as SelectItem[];
    }
    return [] as SelectItem[];
  }, [employeesOwnersSearch]);

  const watchedOwnerIds = watch("owner_ids");
  const [inviteFromOptionEmployeeIds, setInviteFromOptionEmployeeIds] = useState<number[]>(
    survey.owners?.map((x) => x.id) ?? []
  );

  useDebounce(
    () => {
      if (!employeeMe) return;
      const uniqueIds = Array.from(new Set([employeeMe.id, ...(watchedOwnerIds ?? [])]));
      setInviteFromOptionEmployeeIds(uniqueIds);
    },
    1000,
    [employeeMe, watchedOwnerIds]
  );

  const { data: inviteFromEmployees, isLoading: inviteFromIsLoading } = useEmployeesByIds(
    inviteFromOptionEmployeeIds
  );

  const inviteFromOptions = useMemo(() => {
    let options: SelectItem[] = [];

    if (inviteFromEmployees) {
      options = inviteFromEmployees.map((x) => ({
        id: x.id,
        title: renderEmployeeName(x),
      }));
    }

    const existsInOptions = options.some(
      (opt) => opt.id === getValues("invitation_from_employee_id")
    );

    if (!existsInOptions) {
      setValue("invitation_from_employee_id", survey.owners[0]?.id);
    }

    return options;
  }, [inviteFromEmployees, survey]);

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit(survey.id)}>
          {t("common:exit")}
        </Button>
      }
      titleSlot={<SurveyEditTitleSlot step={SurveyEditPageStep.details} />}
      isCenterVertical={false}
      actionsSlot={
        <>
          <Button size={"lg"} onClick={onSubmit}>
            {t("common:continue")}
          </Button>
        </>
      }
      sidebarSlot={<SurveyEditSidebar step={SurveyEditPageStep.details} survey={survey} />}
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"sm"} justify={"center"}>
          <Stack className={"card-panel"} gap={"2xl"}>
            {useEmoji && (
              <Stack>
                <Controller
                  control={control}
                  name={"emoji"}
                  render={({ field: { value, onChange } }) => (
                    <EmojiPickerDropdown
                      onEmojiSelect={(em) => {
                        onChange(em);
                      }}
                    >
                      <Box
                        className={
                          "relative w-41 h-41 group cursor-pointer rounded-md bg-brand-dark/5 shadow-lg"
                        }
                      >
                        {value && (
                          <Avatar variant={"plain"} url={undefined} emoji={value} size={"2xl"} />
                        )}
                        <Box
                          className={
                            "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-md flex items-center justify-center"
                          }
                        >
                          <FontAwesomeIcon
                            icon={"fa-light fa-icons"}
                            className={"text-[74px] text-light "}
                          />
                        </Box>
                      </Box>
                    </EmojiPickerDropdown>
                  )}
                />

                {errors.emoji && (
                  <Box className="pt-2 pb-4">
                    <span className="text-danger">{errors.emoji.message}</span>
                  </Box>
                )}
              </Stack>
            )}
            <Stack gap={"lg"}>
              <Input
                type={"text"}
                label={t("common:title")}
                size={"lg"}
                {...register("title")}
                error={errors.title?.message}
              />
              <Textarea
                label={t("common:description")}
                {...register("description")}
                error={errors.description?.message}
              />
              <Switch
                checked={!useEmoji}
                onChange={(value) => {
                  setUseEmoji(!value);
                }}
                title={t("surveys:do_not_use_emoji")}
              />
              {isFeatureEnabled(ApiFeatureType.talent_surveys_ai) && (
                <Controller
                  control={control}
                  name={"use_ai"}
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      checked={value}
                      onChange={onChange}
                      title={t("surveys:use_ai_title")}
                      description={t("surveys:use_ai_description")}
                    />
                  )}
                />
              )}
            </Stack>
          </Stack>
          <Stack className={"card-panel"} gap={"2xl"}>
            <Title header={t("common:participants")} size={"md"} />
            <Stack gap={"md"}>
              <RuleGroupsEditor
                control={control}
                fieldName={"participants_rule_groups"}
                setValue={setValue}
                place={RuleRegistryPlace.surveyParticipants}
                error={errors.participants_rule_groups?.message}
                conditionTypes={participantConditionTypes.conditionTypes}
                maxCardsPerRow={2}
              />
            </Stack>
          </Stack>
          <Stack className={"card-panel"} gap={"2xl"}>
            <Title header={t("surveys:survey_share_title")} size={"md"} />
            <Switch
              checked={useEmployeeSharing}
              onChange={(value) => {
                setUseEmployeeSharing(value);
              }}
              title={t("surveys:share_specific_people_title")}
              description={t("surveys:share_specific_people_description")}
            />
            {useEmployeeSharing && (
              <Stack gap={"lg"}>
                <Stack gap={"md"}>
                  <RuleGroupsEditor
                    control={control}
                    fieldName={"share_employee_rule_groups"}
                    setValue={setValue}
                    place={RuleRegistryPlace.surveyShareEmployees}
                    error={errors.share_employee_rule_groups?.message}
                    conditionTypes={shareEmployeeConditionTypes.conditionTypes}
                    maxCardsPerRow={2}
                  />
                </Stack>
                <Controller
                  control={control}
                  name={"is_share_employee_after_completion"}
                  render={({ field }) => (
                    <Checkbox
                      title={t("surveys:share_after_completion_title")}
                      caption={t("surveys:share_after_completion_description")}
                      checked={field.value}
                      onChange={() => field.onChange(!field.value)}
                    />
                  )}
                />
              </Stack>
            )}
            {canManageOwners && (
              <Stack gap={"md"}>
                <Switch
                  checked={useInviteCoOwners}
                  onChange={(value) => {
                    setUseInviteCoOwners(value);
                  }}
                  title={t("surveys:invite_co_owners_title")}
                  description={t("surveys:invite_co_owners_description")}
                />
                {useInviteCoOwners && (
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <Multiselect
                        label={t("common:co_owners")}
                        emptyTitle={t("common:select")}
                        values={value ?? []}
                        options={ownerOptions}
                        searchable
                        loadOptionsOnOpen
                        searchOptions={employeesOwnersSearchOptions}
                        isLoading={isEmployeesOwnerIsLoading}
                        onSearchOptions={(value) => {
                          setEmployeeOwnersFilter({
                            ...employeeOwnersFilter,
                            text: value,
                          });
                        }}
                        onChange={onChange}
                        error={errors.owner_ids?.message}
                      />
                    )}
                    control={control}
                    name={"owner_ids"}
                  />
                )}
              </Stack>
            )}
          </Stack>
          <Stack className={"card-panel"} gap={"2xl"}>
            <Title
              header={t("common:invitation")}
              caption={t("surveys:invitation_description")}
              size={"md"}
            />
            <Stack gap={"md"}>
              <Box>
                <Controller
                  render={({ field: { value, onChange } }) => (
                    <Select
                      label={t("common:invite_from")}
                      emptyTitle={t("common:select")}
                      value={value}
                      options={inviteFromOptions}
                      searchable
                      isLoading={inviteFromIsLoading}
                      onChange={onChange}
                      error={errors.invitation_from_employee_id?.message}
                    />
                  )}
                  control={control}
                  name={"invitation_from_employee_id"}
                />
                <Input
                  label={t("common:invitation_subject")}
                  type="text"
                  {...register("invitation_subject")}
                  error={errors.invitation_subject?.message}
                />
              </Box>
              <Controller
                control={control}
                name={"invitation_body"}
                render={({ field: { value, onChange } }) => (
                  <RichEditor
                    value={value ?? ""}
                    placeholder={t("core:enter_invitation_body_text") + "..."}
                    onChange={onChange}
                    minHeight={100}
                    error={errors.invitation_body?.message}
                  />
                )}
              />
            </Stack>
          </Stack>
          <Button type={"submit"} size={"lg"} isLoading={surveyUpdate.isPending}>
            {t("common:continue")}
          </Button>
        </Stack>
      </form>
    </FullScreenPage>
  );
};
